/**
 * Bucket Workflow API Client
 * Handles API calls for the independent execution workspace (Bucket List)
 * 
 * Confirmed Plans = Independent snapshots that don't depend on original Plan data
 */

import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Get auth headers from localStorage
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Bucket Workflow API
 */
export const bucketWorkflowApi = {
  /**
   * Create a new confirmed plan with full snapshot path
   * Called from Plan Step 3 when user confirms campaigns
   * 
   * @param {Object} data - Confirmation data including snapshots
   * @param {string} data.asset_id - Asset ID
   * @param {string} data.asset_name - Asset name
   * @param {string} data.source_plan_id - Original plan ID (for trace only)
   * @param {string} data.source_plan_name - Original plan name
   * @param {Object} data.step0_goal_offer - Step 0 snapshot
   * @param {Object} data.step1_personas - Step 1 snapshot (selected personas)
   * @param {Object} data.step2_segments - Step 2 snapshot (selected segments)
   * @param {Object} data.step3_constraints - Step 3 snapshot (company stage, channels, etc.)
   * @param {Array} data.selected_campaigns - Campaign plays to snapshot
   * @param {string} data.idempotency_key - Optional idempotency key
   */
  createConfirmedPlan: async (data) => {
    const response = await axios.post(
      `${API}/api/bucket-workflow/confirmed-plans`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Legacy create bucket - redirects to new confirmed plan system
   * Maintained for backward compatibility
   */
  createBucket: async (data) => {
    // Map to new format with snapshot fields if available
    const payload = {
      plan_id: data.plan_id,
      plan_name: data.plan_name,
      asset_id: data.asset_id,
      asset_name: data.asset_name,
      selected_campaigns: data.selected_campaigns,
      idempotency_key: data.idempotency_key,
      // New snapshot fields
      step0_goal_offer: data.step0_goal_offer,
      step1_personas: data.step1_personas,
      step2_segments: data.step2_segments,
      step3_constraints: data.step3_constraints
    };
    
    const response = await axios.post(
      `${API}/api/bucket-workflow/buckets`,
      payload,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Get all confirmed plans for the current user
   * @param {string} assetId - Optional asset ID to filter by
   */
  getConfirmedPlans: async (assetId = null) => {
    const params = assetId ? { asset_id: assetId } : {};
    const response = await axios.get(
      `${API}/api/bucket-workflow/confirmed-plans`,
      { headers: getAuthHeaders(), params }
    );
    return response.data;
  },

  /**
   * Get all buckets (legacy format)
   * @param {string} assetId - Optional asset ID to filter by
   */
  getBuckets: async (assetId = null) => {
    const params = assetId ? { asset_id: assetId } : {};
    const response = await axios.get(
      `${API}/api/bucket-workflow/buckets`,
      { headers: getAuthHeaders(), params }
    );
    return response.data;
  },

  /**
   * Get all buckets WITH their campaigns in a single optimized request
   * Avoids N+1 queries - use this for the main Bucket List page
   * @param {string} assetId - Optional asset ID to filter by
   */
  getBucketsWithCampaigns: async (assetId = null) => {
    const params = assetId ? { asset_id: assetId } : {};
    const response = await axios.get(
      `${API}/api/bucket-workflow/buckets-with-campaigns`,
      { headers: getAuthHeaders(), params }
    );
    return response.data;
  },

  /**
   * Get confirmed plan detail with campaigns and snapshot path
   */
  getConfirmedPlanDetail: async (confirmedPlanId) => {
    const response = await axios.get(
      `${API}/api/bucket-workflow/confirmed-plans/${confirmedPlanId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Get bucket detail (legacy format)
   */
  getBucketDetail: async (bucketId) => {
    const response = await axios.get(
      `${API}/api/bucket-workflow/buckets/${bucketId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Update confirmed plan (status, title, notes)
   */
  updateConfirmedPlan: async (confirmedPlanId, data) => {
    const response = await axios.patch(
      `${API}/api/bucket-workflow/confirmed-plans/${confirmedPlanId}`,
      data,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Update bucket status (legacy format, for drag-and-drop)
   */
  updateBucketStatus: async (bucketId, status) => {
    const response = await axios.patch(
      `${API}/api/bucket-workflow/buckets/${bucketId}`,
      { status },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Delete a confirmed plan and all its campaigns
   * This is INDEPENDENT - does NOT affect original Plan data
   */
  deleteConfirmedPlan: async (confirmedPlanId) => {
    const response = await axios.delete(
      `${API}/api/bucket-workflow/confirmed-plans/${confirmedPlanId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Delete a bucket (legacy format)
   */
  deleteBucket: async (bucketId) => {
    const response = await axios.delete(
      `${API}/api/bucket-workflow/buckets/${bucketId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Delete a single confirmed campaign from a plan
   * This is INDEPENDENT - does NOT affect original Plan data
   */
  deleteConfirmedCampaign: async (confirmedCampaignId) => {
    const response = await axios.delete(
      `${API}/api/bucket-workflow/confirmed-campaigns/${confirmedCampaignId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }
};

export default bucketWorkflowApi;
